




















import type {
  Comment,
  CommentHeight,
  CommentMargins,
} from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import type { PropType, Ref } from '@vue/composition-api';
import { defineComponent, ref, toRefs, watch } from '@vue/composition-api';
import Vue from 'vue';
import { CommentHelper } from '../helpers/comment.helper';

import CommentCard from './CommentCard.vue';

export default defineComponent({
  name: 'Comments',
  components: {
    CommentCard,
  },
  props: {
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    selectedId: {
      type: String,
      default: '',
    },
    dimensionsUpdating: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const commentsMargins: Ref<CommentMargins> = ref({});

    const reset = () => {
      commentsMargins.value = {};
      commentsHeights.value = {};
    };

    const { comments, dimensionsUpdating, selectedId } = toRefs(props);

    watch(
      () => comments.value.length,
      () => {
        reset();
      }
    );
    const commentsHeights: Ref<CommentHeight> = ref({});

    const addHeight = ({ commentId, height }) => {
      Vue.set(commentsHeights.value, commentId, height);
    };

    watch(dimensionsUpdating, updating => {
      if (!updating) {
        reset();
      }
    });

    const marginOffset = 16; // We shouldnt calculate the offset like this

    const calculateMarkTagDistanceToTop = (
      dataCommentId: string,
      currentPageDistanceToTop: number
    ): number => {
      const commentMarkTag: HTMLElement = document.querySelector(
        `[data-comment-id="${dataCommentId}"]`
      );
      if (!commentMarkTag) {
        return;
      }
      const commentMarkDistanceToTop = commentMarkTag.getBoundingClientRect().top;
      return commentMarkDistanceToTop - currentPageDistanceToTop - marginOffset;
    };

    const commentsContainer: Ref<HTMLElement> = ref(null);
    const minimumMargin = 10;

    const setFirstCommentMargins = (commentId: string, margin: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired: margin,
        actual: margin,
        selection: margin,
      });
    };

    const setMinimumMargins = (commentId: string, desired: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired,
        actual: minimumMargin,
        selection: minimumMargin,
      });
    };

    const setNewMargins = (commentId: string, newMargin: number, desired: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired,
        actual: newMargin,
        selection: newMargin,
      });
    };

    watch(commentsHeights, value => {
      if (Object.keys(value).length !== comments.value.length) {
        return;
      }

      let totalHeight = 0;
      comments.value.forEach((comment: Comment, index: number) => {
        const distanceToTop = calculateMarkTagDistanceToTop(
          comment.id,
          commentsContainer.value.getBoundingClientRect().top
        );

        if (index === 0) {
          setFirstCommentMargins(comment.id, distanceToTop);
          totalHeight = distanceToTop + commentsHeights.value[comment.id];
          return;
        }

        const newMargin = distanceToTop - totalHeight;

        if (newMargin < minimumMargin) {
          setMinimumMargins(comment.id, distanceToTop);
          totalHeight += minimumMargin + commentsHeights.value[comment.id];
          return;
        }
        setNewMargins(comment.id, newMargin, distanceToTop);
        totalHeight += newMargin + commentsHeights.value[comment.id];
      }, 0);

      if (selectedId.value) {
        adjustMarginsDuringSelection();
      }
    });

    const resetCommentsSelectionMargins = () =>
      Object.keys(commentsMargins.value).forEach(
        (id: string) => (commentsMargins.value[id].selection = commentsMargins.value[id].actual)
      );

    const getCombinedDistanceFromTop = (commentIds: string[], actual: number) =>
      commentIds.reduce(
        (distance: number, id: string) =>
          distance + commentsHeights.value[id] + commentsMargins.value[id].actual,
        0
      ) + actual;

    const increaseMarginForFollowingComments = (commentId: string, marginToAdd: number) => {
      const commentIds = CommentHelper.getCommentIdsAfterSelectedComment(commentId, comments.value);
      if (!commentIds.length) {
        return;
      }
      let marginAdded = false;
      commentIds.forEach((id: string) => {
        if (marginAdded) {
          return;
        }
        const previousCommentIds = CommentHelper.getReversedCommentIdsBeforeId(id, comments.value);
        const combinedDistance = getCombinedDistanceFromTop(
          previousCommentIds,
          commentsMargins.value[id].actual
        );
        if (combinedDistance < commentsMargins.value[id].desired) {
          // Comment is being pushed down due to close neighbours - don't add margins
          return;
        }
        commentsMargins.value[id].selection = commentsMargins.value[id].actual + marginToAdd;
        marginAdded = true;
      });
    };

    const adjustMarginsDuringSelection = () => {
      resetCommentsSelectionMargins();
      const { desired, actual } = commentsMargins.value[selectedId.value];

      if (desired === actual) {
        commentsMargins.value[selectedId.value].selection = desired;
        return;
      }

      const previousCommentIds = CommentHelper.getReversedCommentIdsBeforeId(
        selectedId.value,
        comments.value
      );

      const distanceFromTop = getCombinedDistanceFromTop(previousCommentIds, actual);
      const distanceToRemove = distanceFromTop - desired;

      increaseMarginForFollowingComments(selectedId.value, distanceToRemove);

      if (previousCommentIds.length === 0) {
        return;
      }

      let distanceLeft = distanceToRemove;
      previousCommentIds.forEach((id: string, index: number) => {
        if (distanceLeft === 0) {
          return;
        }

        if (CommentHelper.evaluateIsIndexFinalComment(index, previousCommentIds.length)) {
          commentsMargins.value[id].selection -= distanceLeft;
          distanceLeft = 0;
          return;
        }

        if (commentsMargins.value[id].actual === minimumMargin) {
          return;
        }

        const allowedMarginToRemove = CommentHelper.getMarginToRemoveFromComment(
          commentsMargins.value[id].actual,
          distanceLeft,
          minimumMargin
        );

        if (allowedMarginToRemove === 0) {
          return;
        }

        commentsMargins.value[id].selection -= allowedMarginToRemove;
        distanceLeft -= allowedMarginToRemove;
      });
    };

    watch(selectedId, (id: string) => {
      if (!id) {
        return;
      }
      adjustMarginsDuringSelection();
    });

    const getMarginTop = (commentId: string) => {
      if (!commentsMargins.value[commentId]) {
        return 0;
      }
      if (selectedId.value) {
        return CommentHelper.evaluateMarginOptions(
          commentsMargins.value[commentId].actual,
          commentsMargins.value[commentId].selection
        );
      }
      return commentsMargins.value[commentId].actual;
    };

    return {
      addHeight,
      getMarginTop,
      commentsContainer,
    };
  },
});
